import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { formatCalendarDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { subjectColor, useSubjectLabel } from './subject-labels';
import { SubjectDot } from './subjects';
import {
  useUsageRetention,
  type CohortCellDto,
  type GroupSubject,
  type Subject,
  type UsageFilters,
} from './usage-analytics.api';
import { formatShare, ratio } from './usage-format';
import { Empty, ErrorNotice } from './usage-parts';

/**
 * Who came back.
 *
 * One subject at a time, unlike the panels above it: a cohort grid is already
 * a table of weeks by weeks, and four of them side by side is a wall. The
 * picker changes which subject is read; the request still covers all of them,
 * so switching is instant.
 *
 * "New" here means first seen in the visits that are still kept, not a new
 * account — the API can only see as far back as it retains, and says so. The
 * help line under the heading repeats it, because a first week where everyone
 * is new is the first thing a reader will ask about.
 */

/** Weeks after the first shown across the grid. Past this the columns stop fitting. */
const MAX_WEEKS_AFTER = 12;

/** The opacity of the "new" half of a bar: the same hue, stepped lighter. */
const NEW_OPACITY = 0.45;

const DAY_MS = 86_400_000;

const dayOf = (value: string) => value.slice(0, 10);
const timeOf = (day: string) => Date.parse(`${day}T00:00:00.000Z`);

/** The Sunday on or before a day, which is where the API starts its weeks. */
function weekOf(day: string) {
  const date = new Date(timeOf(day));
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return date.toISOString().slice(0, 10);
}

interface Week {
  week: string;
  fresh: number;
  returning: number;
}

function WeeklyBars({
  weeks,
  color,
}: {
  weeks: Week[];
  color: string;
}) {
  const { t } = useTranslation();
  const max = Math.max(1, ...weeks.map((week) => week.fresh + week.returning));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <ul className="flex gap-4 text-xs">
          <li className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 rounded-sm"
              style={{ backgroundColor: color }}
            />
            {t('usage.returning')}
          </li>
          <li className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="size-2.5 rounded-sm"
              style={{ backgroundColor: color, opacity: NEW_OPACITY }}
            />
            {t('usage.newAccounts')}
          </li>
        </ul>
        <span className="text-muted-foreground text-xs tabular-nums">
          {t('usage.tallestBar', { formatted: formatNumber(max) })}
        </span>
      </div>

      <div className="flex h-36 items-end gap-0.5">
        {weeks.map((week) => {
          const total = week.fresh + week.returning;
          return (
            <Tooltip key={week.week}>
              <TooltipTrigger asChild>
                {/* The whole column is the target, not the bar: a short week
                    would otherwise be a few pixels to aim at. */}
                <div className="hover:bg-foreground/5 flex h-full min-w-0 flex-1 flex-col justify-end gap-0.5 rounded-sm">
                  {week.fresh > 0 && (
                    <div
                      className="rounded-t-sm"
                      style={{
                        height: `${(week.fresh / max) * 100}%`,
                        backgroundColor: color,
                        opacity: NEW_OPACITY,
                      }}
                    />
                  )}
                  {week.returning > 0 && (
                    <div
                      className={cn(week.fresh === 0 && 'rounded-t-sm')}
                      style={{
                        height: `${(week.returning / max) * 100}%`,
                        backgroundColor: color,
                      }}
                    />
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-semibold">
                  {t('usage.weekOf', { date: formatCalendarDate(week.week) })}
                </p>
                <p>
                  {t('usage.returning')}: {formatNumber(week.returning)}
                </p>
                <p>
                  {t('usage.newAccounts')}: {formatNumber(week.fresh)}
                </p>
                <p className="text-muted-foreground">
                  {t('usage.activeUsers')}: {formatNumber(total)}
                </p>
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>

      <div className="text-muted-foreground flex justify-between text-xs">
        <span>{formatCalendarDate(weeks[0].week)}</span>
        {weeks.length > 1 && (
          <span>{formatCalendarDate(weeks[weeks.length - 1].week)}</span>
        )}
      </div>
    </div>
  );
}

function CohortGrid({
  cohorts,
  cells,
  lastWeek,
  color,
}: {
  cohorts: string[];
  /** Users by `cohort|weeksAfter`. */
  cells: Map<string, number>;
  lastWeek: string;
  color: string;
}) {
  const { t } = useTranslation();

  const span = Math.min(
    MAX_WEEKS_AFTER,
    Math.max(
      0,
      ...cohorts.map((cohort) =>
        Math.round((timeOf(lastWeek) - timeOf(cohort)) / (7 * DAY_MS))
      )
    )
  );
  const offsets = Array.from({ length: span }, (_, index) => index + 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[34rem] border-separate border-spacing-0.5 text-xs">
        <thead>
          <tr className="text-muted-foreground">
            <th className="pr-2 text-left font-normal">{t('usage.firstSeen')}</th>
            <th className="px-2 text-right font-normal">{t('usage.members')}</th>
            {offsets.map((offset) => (
              <th key={offset} className="w-12 text-center font-normal">
                +{offset}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((cohort) => {
            const size = cells.get(`${cohort}|0`) ?? 0;
            const weeksSince = Math.round(
              (timeOf(lastWeek) - timeOf(cohort)) / (7 * DAY_MS)
            );
            return (
              <tr key={cohort}>
                <th className="pr-2 text-left font-normal whitespace-nowrap">
                  {formatCalendarDate(cohort)}
                </th>
                <td className="px-2 text-right tabular-nums">
                  {formatNumber(size)}
                </td>
                {offsets.map((offset) => {
                  // A week that has not happened yet is not a week nobody
                  // came back in, and must not be drawn like one.
                  if (offset > weeksSince) return <td key={offset} />;
                  const users = cells.get(`${cohort}|${offset}`) ?? 0;
                  const kept = ratio(users, size);
                  return (
                    <td
                      key={offset}
                      className="relative h-7 text-center tabular-nums"
                      title={t('usage.cohortCell', {
                        users: formatNumber(users),
                        size: formatNumber(size),
                      })}
                    >
                      {/* The tint is a layer under the figure rather than the
                          cell's own background, so the figure keeps its ink
                          at every strength of tint. */}
                      <span
                        aria-hidden
                        className={cn(
                          'absolute inset-0 rounded-sm',
                          users === 0 && 'bg-muted'
                        )}
                        style={
                          users > 0
                            ? {
                                backgroundColor: color,
                                opacity: 0.12 + 0.5 * kept,
                              }
                            : undefined
                        }
                      />
                      <span className="relative">
                        {users > 0 ? formatShare(kept).replace('.0%', '%') : '—'}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function UsageRetention({
  filters,
  subjects,
  onOpenLapsed,
}: {
  filters: UsageFilters;
  subjects: Subject[];
  onOpenLapsed: (subject: GroupSubject) => void;
}) {
  const { t } = useTranslation();
  const label = useSubjectLabel();
  const retention = useUsageRetention(filters, subjects);

  const [picked, setPicked] = useState<string | null>(null);
  // Falls back rather than holding a key the reader has since removed.
  const subject =
    subjects.find((entry) => entry.key === picked) ?? subjects[0] ?? null;

  const data = useMemo(
    () => retention.data?.find((entry) => entry.key === subject?.key),
    [retention.data, subject?.key]
  );

  const view = useMemo(() => {
    const all: CohortCellDto[] = data?.cohorts ?? [];
    const firstWeek = weekOf(filters.start);
    const lastWeek = weekOf(filters.end);

    const byWeek = new Map<string, Week>();
    const cells = new Map<string, number>();
    const cohorts = new Set<string>();

    for (const cell of all) {
      const cohort = dayOf(cell.cohortWeek);
      const active = dayOf(cell.activeWeek);

      // The bars cover the range; who counts as new in them was decided
      // against everything before it, which is why the request reaches back.
      if (active >= firstWeek && active <= lastWeek) {
        const week = byWeek.get(active) ?? { week: active, fresh: 0, returning: 0 };
        if (cohort === active) week.fresh += cell.users;
        else week.returning += cell.users;
        byWeek.set(active, week);
      }

      if (cohort >= firstWeek && cohort <= lastWeek) {
        cohorts.add(cohort);
        const offset = Math.round((timeOf(active) - timeOf(cohort)) / (7 * DAY_MS));
        cells.set(`${cohort}|${offset}`, cell.users);
      }
    }

    return {
      weeks: [...byWeek.values()].sort((a, b) => a.week.localeCompare(b.week)),
      cohorts: [...cohorts].sort(),
      cells,
      lastWeek,
    };
  }, [data, filters.start, filters.end]);

  if (retention.isError) {
    return (
      <ErrorNotice
        message={retention.error?.message || t('usage.loadFailed')}
        onRetry={() => void retention.refetch()}
      />
    );
  }
  if (retention.isLoading || !subject) {
    return <Skeleton className="h-72 rounded-xl" />;
  }

  const color = subjectColor(subject);
  const lapsed = data?.lapsed ?? null;

  return (
    <div className="relative rounded-xl">
      <BusyOverlay show={retention.isFetching} />
      <div
        className={cn(
          'bg-card space-y-5 rounded-xl border p-3',
          retention.isFetching && 'opacity-60 transition-opacity'
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          {subjects.length > 1 ? (
            <Select value={subject.key} onValueChange={setPicked}>
              <SelectTrigger
                className="w-full sm:w-72"
                aria-label={t('usage.subject')}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {subjects.map((entry) => (
                  <SelectItem key={entry.key} value={entry.key}>
                    <span className="flex items-center gap-2">
                      <SubjectDot subject={entry} />
                      {label(entry)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="flex min-w-0 items-center gap-2">
              <SubjectDot subject={subject} />
              <span className="truncate text-sm font-medium">{label(subject)}</span>
            </span>
          )}

          {lapsed !== null && (
            <div className="flex items-center gap-2 text-sm">
              <span>
                {t('usage.lapsedCount', {
                  count: lapsed,
                  formatted: formatNumber(lapsed),
                })}
              </span>
              {subject.kind === 'group' && lapsed > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onOpenLapsed(subject)}
                >
                  {t('usage.viewLapsed')}
                </Button>
              )}
            </div>
          )}
        </div>

        {view.weeks.length === 0 ? (
          <Empty>{t('usage.noVisits')}</Empty>
        ) : (
          <>
            <WeeklyBars weeks={view.weeks} color={color} />

            {view.cohorts.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-muted-foreground text-xs font-medium">
                  {t('usage.cohortTitle')}
                </h4>
                <CohortGrid
                  cohorts={view.cohorts}
                  cells={view.cells}
                  lastWeek={view.lastWeek}
                  color={color}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
